/**
 * useGuestTodoOperations — Todo operations for guest goals.
 */
import { formatDateForDatabase } from "@/hooks/goal-operations/helpers/dateUtils";
import { Goal } from "@/types/todo";
import { logger } from "@/utils/logger";

export const useGuestTodoOperations = (
  goals: Goal[],
  setGoals: React.Dispatch<React.SetStateAction<Goal[]>>
) => {
  const addTodo = ({ goalTitle, todoText, todoDueDate }: { goalTitle: string, todoText: string, todoDueDate: string }) => {
    const formattedDate = formatDateForDatabase(todoDueDate);
    logger.debug(`[GuestTodoOps] Adding todo to goal "${goalTitle}" with due date: ${formattedDate}`);
    
    setGoals(prevGoals =>
      prevGoals.map(goal =>
        goal.title === goalTitle
          ? {
              ...goal,
              todos: [...goal.todos, { 
                id: crypto.randomUUID(), 
                text: todoText, 
                completed: false, 
                dueDate: formattedDate || undefined, 
                subtasks: [] 
              }],
            }
          : goal
      )
    );
    return Promise.resolve();
  };

  const deleteTodo = ({ goalTitle, todoId }: { goalTitle: string, todoId: string }) => {
    logger.debug(`[GuestTodoOps] Deleting todo ${todoId} from goal "${goalTitle}"`);
    
    setGoals(prevGoals =>
      prevGoals.map(goal =>
        goal.title === goalTitle
          ? {
              ...goal,
              todos: goal.todos.filter(todo => todo.id !== todoId),
            }
          : goal
      )
    );
    return Promise.resolve();
  };

  const toggleTodo = ({ goalTitle, todoId }: { goalTitle: string, todoId: string }) => {
    logger.debug(`[GuestTodoOps] Toggling todo ${todoId} in goal "${goalTitle}"`);
    
    setGoals(prevGoals =>
      prevGoals.map(goal =>
        goal.title === goalTitle
          ? {
              ...goal,
              todos: goal.todos.map(todo =>
                todo.id === todoId ? { ...todo, completed: !todo.completed } : todo
              ),
            }
          : goal
      )
    );
    return Promise.resolve();
  };

  const updateTodoDueDate = async ({ goalTitle, todoId, newDate }: { goalTitle: string, todoId: string, newDate: Date | string | null }): Promise<any> => {
    logger.debug("[GuestTodoOps] Updating todo due date:", { goalTitle, todoId, newDate });
    
    const formattedDate = formatDateForDatabase(newDate);
    
    setGoals(currentGoals => 
      currentGoals.map(goal => {
        if (goal.title === goalTitle) {
          return {
            ...goal,
            todos: goal.todos.map(todo => {
              if (todo.id === todoId) {
                return {
                  ...todo,
                  dueDate: formattedDate || undefined
                };
              }
              return todo;
            })
          };
        }
        return goal;
      })
    );
    
    return Promise.resolve({ todoId, formattedDate });
  };

  return {
    addTodo,
    deleteTodo,
    toggleTodo,
    updateTodoDueDate
  };
};
